import type { TFunction } from "i18next";
import { z } from "zod";
import {
  MAX_ACTUAL_SIZE_LENGTH,
  MAX_HOLD,
  MIN_HOLD,
  PLANNED_QUANTITY_PATTERN,
} from "./SequencePanel.constants";

const F = "drybulk-cargo-operations.sequences.fields";
const V = "drybulk-cargo-operations.sequences.validation";

const mandatoryCode = (t: TFunction, labelKey: string) =>
  z
    .string()
    .trim()
    .min(1, t("form-validation.mandatory", { label: t(`${F}.${labelKey}`) }));

export const sequencePanelSchema = (t: TFunction) =>
  z.object({
    plannedQuantity: z
      .string()
      .trim()
      .min(1, t("form-validation.mandatory", { label: t(`${F}.quantity`) }))
      .refine(
        (value) => value === "" || (PLANNED_QUANTITY_PATTERN.test(value) && Number(value) > 0),
        t(`${V}.quantity`),
      ),
    hold: z
      .string()
      .min(1, t("form-validation.mandatory", { label: t(`${F}.hold`) }))
      .refine(
        (value) => value === "" || (Number(value) >= MIN_HOLD && Number(value) <= MAX_HOLD),
        t(`${V}.hold`),
      ),
    shipperCompanyCode: mandatoryCode(t, "shipper"),
    stockPileCode: mandatoryCode(t, "stock-pile"),
    cargoCode: mandatoryCode(t, "cargo-code"),
    gradeCode: z.string().optional(),
    gradeSizeCode: mandatoryCode(t, "grade-size"),
    actualSize: z
      .string()
      .trim()
      .max(
        MAX_ACTUAL_SIZE_LENGTH,
        t("form-validation.max-length", {
          label: t(`${F}.actual-size`),
          max: MAX_ACTUAL_SIZE_LENGTH,
        }),
      )
      .optional(),
  });

export type SequencePanelFormData = z.infer<ReturnType<typeof sequencePanelSchema>>;

export const sequencePanelDefaultValues: SequencePanelFormData = {
  plannedQuantity: "",
  hold: "",
  shipperCompanyCode: "",
  stockPileCode: "",
  cargoCode: "",
  gradeCode: "",
  gradeSizeCode: "",
  actualSize: "",
};
